import { useMemo, useState } from 'react'
import type { DiffEntry, Question, Section, VersionDiff } from '../lib/api'
import { CompareTable, Status, type CompareColumn, type CompareFilter, type CompareGroup } from './kit'

// A questionnaire as a table, and two of its versions compared as a table
// (release 3.3, lane 2: the mentor's "too verbose", and "assessments and
// questionnaires shorter and tabular").
//
// # WHAT IT REPLACES
//
// The published questionnaire was a card per section and a block per
// question, with the answer type, required, N/A, evidence and every citation
// as chips underneath: 68 questions were 8,869 px at 1440 x 900 (ten windows)
// and 3,205 words. The rule that skips a question (visible_when, the smart
// skipping the vendor portal applies) was not shown at all, so a reader could
// not tell which questions a vendor may never see. Now it is one row a
// question in a table that scrolls in its own panel: the question in two
// lines, the answer it takes, its rules, when it is shown, and what it maps
// to. Sections fold; the filters say how many questions need evidence, are
// shown only on a condition, or are optional.
//
// The version compare was a list per change type, each under a sentence; it
// is the same table, a group per change type, from and to side by side.

// "Shown when PR-02 is Yes": a condition in the questionnaire's own words,
// with the other question's option labels where it has them.
export function shownWhen(q: Question, byKey: Map<string, Question>): string {
  const w = q.visibleWhen
  if (!w || !w.question_key) return 'Always'
  const other = byKey.get(w.question_key)
  const label = (v: string) => other?.options?.find((o) => o.value === v)?.label || v
  const values = [...new Set(w.in ?? [])].map(label)
  // A rule naming no answers hides the question from every vendor (the portal
  // shows it only for a listed answer).
  if (values.length === 0) return 'Never shown (the rule lists no answers)'
  const said = values.length <= 1 ? (values[0] ?? 'answered') : values.slice(0, -1).join(', ') + ' or ' + values[values.length - 1]
  return 'When ' + w.question_key + ' is ' + said
}

// The answer a question takes, short: the type, and the choices when there
// are a few.
export function answerWords(q: Question, answerName: (code: string | undefined) => string): string {
  const type = answerName(q.answerType)
  const opts = (q.options ?? []).map((o) => o.label || o.value).filter(Boolean)
  if (opts.length === 0) return type
  if (opts.length <= 4) return type + ': ' + opts.join(' / ')
  return type + ': ' + opts.length + ' choices'
}

export type QuestionFilter = 'all' | 'evidence' | 'conditional' | 'optional'

export function questionFilters(sections: Section[]): CompareFilter<QuestionFilter>[] {
  const all = sections.flatMap((s) => s.questions)
  return [
    { key: 'all', label: 'All', count: all.length },
    { key: 'evidence', label: 'Evidence needed', count: all.filter((q) => q.evidenceRequired).length },
    { key: 'conditional', label: 'Shown on a condition', count: all.filter((q) => q.visibleWhen?.question_key).length },
    { key: 'optional', label: 'Optional', count: all.filter((q) => !q.isRequired).length },
  ]
}

function keep(q: Question, f: QuestionFilter): boolean {
  if (f === 'evidence') return q.evidenceRequired
  if (f === 'conditional') return Boolean(q.visibleWhen?.question_key)
  if (f === 'optional') return !q.isRequired
  return true
}

// What a section's header says: its count (as "3 of 12" when a filter shows
// fewer), what in it is conditional or needs evidence, its risk type, and its
// description.
export function sectionSummary(s: Section, typeName: (code: string | undefined) => string, shown?: number): string {
  const n = s.questions.length
  const parts = [(shown != null && shown !== n ? shown + ' of ' : '') + n + (n === 1 ? ' question' : ' questions')]
  const cond = s.questions.filter((q) => q.visibleWhen?.question_key).length
  const ev = s.questions.filter((q) => q.evidenceRequired).length
  if (cond > 0) parts.push(cond + ' on a condition')
  if (ev > 0) parts.push(ev + ' need evidence')
  if (s.riskType) parts.push(typeName(s.riskType))
  return parts.join(', ') + (s.description ? '. ' + s.description : '')
}

export function QuestionTable({
  sections,
  typeName,
  answerName,
}: {
  sections: Section[]
  // The name of a section's risk type, and of a question's answer type.
  typeName: (code: string | undefined) => string
  answerName: (code: string | undefined) => string
}) {
  const [filter, setFilter] = useState<QuestionFilter>('all')
  const [folded, setFolded] = useState<ReadonlySet<string>>(() => new Set())
  const byKey = useMemo(() => new Map(sections.flatMap((s) => s.questions).map((q) => [q.questionKey, q])), [sections])
  const filters = useMemo(() => questionFilters(sections), [sections])

  const groups: CompareGroup<Question>[] = sections
    .map((s, i) => {
      const rows = s.questions.filter((q) => keep(q, filter))
      return { key: s.id, title: i + 1 + '. ' + s.title, summary: sectionSummary(s, typeName, rows.length), rows }
    })
    // Under All every section stays, an empty one too; a filter shows only
    // the sections it found something in.
    .filter((g) => filter === 'all' || g.rows.length > 0)

  const columns: CompareColumn<Question>[] = [
    {
      key: 'prompt',
      label: 'Question',
      clamp: true,
      className: 'qt-prompt',
      cell: (q) => (
        <>
          {q.prompt}
          {q.helpText && <span className="qt-help"> {q.helpText}</span>}
        </>
      ),
    },
    { key: 'answer', label: 'Answer', className: 'qt-answer', cell: (q) => answerWords(q, answerName) },
    {
      key: 'rules',
      label: 'Rules',
      className: 'qt-rules',
      info: 'Required questions must be answered to submit. N/A lets the vendor say it does not apply. Evidence needed asks for a file with the answer.',
      cell: (q) => (
        <span className="qt-chips">
          {q.isRequired ? <span className="chip strong">Required</span> : <span className="chip">Optional</span>}
          {q.allowsNa && <span className="chip">N/A</span>}
          {q.evidenceRequired && <span className="chip warn">Evidence</span>}
        </span>
      ),
    },
    {
      key: 'shown',
      label: 'Shown',
      className: 'qt-shown',
      info: 'A question shown on a condition is skipped for a vendor whose earlier answer does not meet it, and a skipped question is not counted in coverage.',
      cell: (q) => {
        const s = shownWhen(q, byKey)
        return s === 'Always' ? <span className="muted">Always</span> : <span className="qt-cond">{s}</span>
      },
    },
    {
      key: 'maps',
      label: 'Maps to',
      clamp: true,
      className: 'qt-maps',
      cell: (q) => (q.citations?.length ? q.citations.join(', ') : <span className="muted">None</span>),
    },
  ]

  return (
    <CompareTable<Question, QuestionFilter>
      label="Questions"
      columns={columns}
      groups={groups}
      rowKey={(q) => q.id}
      itemLabel={(q) => ({ key: q.questionKey, label: q.questionKey })}
      filters={filters}
      filter={filter}
      onFilter={setFilter}
      folded={folded}
      onFold={(g, fold) =>
        setFolded((prev) => {
          const next = new Set(prev)
          if (fold) next.add(g)
          else next.delete(g)
          return next
        })
      }
      empty={filter === 'all' ? 'This version has no questions.' : 'No question matches this filter.'}
    />
  )
}

// The change types of a version compare, in the order a reader acts on them,
// each with its consequence said once on the group's line.
type DiffKey = 'added' | 'removed' | 'reworded' | 'retyped' | 'reweighted' | 'moved' | 'conditioned' | 'ruled'

export const DIFF_GROUPS: { key: DiffKey; title: string; says: string }[] = [
  { key: 'removed', title: 'Removed', says: 'a weight on a removed key refuses the publish' },
  { key: 'added', title: 'Added', says: 'scored at nothing until the model weighs them' },
  { key: 'conditioned', title: 'When it is shown', says: 'a vendor may now skip it, or now see it' },
  { key: 'ruled', title: 'Answer rules changed', says: 'required, N/A or evidence differs' },
  { key: 'retyped', title: 'Answer type changed', says: 'an answer given under the old type may not parse' },
  { key: 'reweighted', title: 'Option scores changed', says: 'the same answer counts a different amount' },
  { key: 'reworded', title: 'Reworded', says: 'answers already given still count' },
  { key: 'moved', title: 'Moved section', says: 'a delta reassessment by section changes scope' },
]

// The one line above the compare: "v1 to v5: 1 reworded, 1 shown differently".
const COUNT_WORDS: Record<string, string> = {
  added: 'added',
  removed: 'removed',
  reworded: 'reworded',
  retyped: 'answer type changed',
  reweighted: 'scores changed',
  moved: 'moved section',
  conditioned: 'shown differently',
  ruled: 'answer rules changed',
}

export function diffSummary(diff: VersionDiff): string {
  const said = DIFF_GROUPS.map((g) => [g.key, diff.counts[g.key] ?? 0] as const)
    .filter(([, n]) => n > 0)
    .map(([k, n]) => n + ' ' + COUNT_WORDS[k])
  return 'v' + diff.from.versionNo + ' to v' + diff.to.versionNo + ': ' + (said.join(', ') || 'nothing changed')
}

type DiffRow = DiffEntry & { change: string }

export function diffGroups(diff: VersionDiff): CompareGroup<DiffRow>[] {
  return DIFF_GROUPS.map((g) => {
    const rows = ((diff[g.key] ?? []) as DiffEntry[]).map((e) => ({ ...e, change: g.key }))
    return { key: g.key, title: g.title, summary: rows.length + ': ' + g.says, rows }
  }).filter((g) => g.rows.length > 0)
}

export function DiffTable({ diff }: { diff: VersionDiff }) {
  const groups = diffGroups(diff)
  const [folded, setFolded] = useState<ReadonlySet<string>>(() => new Set())
  const columns: CompareColumn<DiffRow>[] = [
    { key: 'prompt', label: 'Question', clamp: true, className: 'qt-prompt', cell: (e) => e.prompt || <span className="muted">-</span> },
    {
      key: 'from',
      label: 'v' + diff.from.versionNo,
      clamp: true,
      cell: (e) => (e.from !== undefined ? e.from || <span className="muted">nothing</span> : <span className="muted">-</span>),
    },
    {
      key: 'to',
      label: 'v' + diff.to.versionNo,
      clamp: true,
      cell: (e) => (e.to !== undefined ? e.to || <span className="muted">nothing</span> : <span className="muted">-</span>),
    },
    {
      key: 'given',
      label: 'Answers given',
      info: 'Answers vendors have already given to this question on open assessments of the older version.',
      className: 'qt-given',
      cell: (e) => (e.inFlightAnswersAffected > 0 ? <Status tone="warn">{String(e.inFlightAnswersAffected)}</Status> : <span className="muted">0</span>),
    },
  ]
  return (
    <CompareTable<DiffRow, never>
      label={'Changes from v' + diff.from.versionNo + ' to v' + diff.to.versionNo}
      columns={columns}
      groups={groups}
      rowKey={(e) => e.change + ':' + e.questionKey}
      itemLabel={(e) => ({ key: e.questionKey, label: e.questionKey })}
      empty="Nothing changed between these versions."
      folded={folded}
      onFold={(g, fold) =>
        setFolded((prev) => {
          const next = new Set(prev)
          if (fold) next.add(g)
          else next.delete(g)
          return next
        })
      }
      maxHeight="60vh"
    />
  )
}
